package com.essntl.features.itinerary.ui.edit_itinerary.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.boundsInParent
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.unit.dp
import com.mohamedrejeb.compose.dnd.DragAndDropContainer
import com.mohamedrejeb.compose.dnd.DragAndDropState
import com.mohamedrejeb.compose.dnd.drag.DraggableItem
import com.mohamedrejeb.compose.dnd.drag.DraggedItemState
import com.mohamedrejeb.compose.dnd.drag.DropStrategy
import com.mohamedrejeb.compose.dnd.drop.dropTarget
import com.mohamedrejeb.compose.dnd.rememberDragAndDropState
import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.itinerary.ui.edit_itinerary.EditItineraryEvent
import com.essntl.features.itinerary.ui.edit_itinerary.EditItineraryNavigation
import com.essntl.features.itinerary.ui.edit_itinerary.EditItineraryState
import com.essntl.features.itinerary.ui.edit_itinerary.rememberSortedItineraryServices
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.key.upDownSelection
import com.essntl.uikit.rememberEquals
import com.essntl.uikit.theme.esColorScheme
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

@Composable
fun ItineraryServicesReorderColumn(
    state: EditItineraryState,
    onEvent: (EditItineraryEvent) -> Unit,
    onNavigation: (EditItineraryNavigation) -> Unit,
    isConcierge: Boolean,
    modifier: Modifier = Modifier,
) {
    val dndState = rememberDragAndDropState<ItineraryServiceModel>()
    val scope = rememberCoroutineScope()
    val scrollState = rememberScrollState()

    val sortedItineraryServicesList = state.rememberSortedItineraryServices(isConcierge = isConcierge)

    var itineraryServicesList by remember(sortedItineraryServicesList) {
        mutableStateOf(sortedItineraryServicesList)
    }

    val isUpdateLoading = remember(state.updateItineraryServiceLoading) {
        state.updateItineraryServiceLoading.isNotEmpty()
    }

    var isConnectServiceDialogVisible by rememberSaveable { mutableStateOf(false) }
    var isDisconnectServiceDialogVisible by rememberSaveable { mutableStateOf(false) }

    var savedFromId by rememberSaveable { mutableStateOf("") }
    var savedToId by rememberSaveable { mutableStateOf("") }
    var savedDropToBottom by rememberSaveable { mutableStateOf(true) }

    fun group(fromId: String, toId: String) {
        if (fromId == toId)
            return

        val fromItem = itineraryServicesList.find { it.id == fromId } ?: return
        val toItem = itineraryServicesList.find { it.id == toId } ?: return

        if (
            fromItem.linkedId == toItem.id ||
            toItem.linkedId == fromItem.id
        )
            return

        savedFromId = fromId
        savedToId = toItem.linkedId.orEmpty().ifEmpty { toId }
        isConnectServiceDialogVisible = true
    }

    fun group() {
        val fromIndex = itineraryServicesList.indexOfFirst { it.id == savedFromId }
        val toIndex = itineraryServicesList.indexOfFirst { it.id == savedToId }

        val targetItem = itineraryServicesList.getOrNull(toIndex) ?: return
        val droppedItem = itineraryServicesList.getOrNull(fromIndex) ?: return

        if (fromIndex != -1 && toIndex != -1) {
            itineraryServicesList = itineraryServicesList
                .toMutableList()
                .apply {
                    val item = droppedItem
                        .copy(
                            linkedId = targetItem.id,
                            startDate = targetItem.startDate,
                            endDate = targetItem.endDate,
                            startTime = targetItem.startTime ?: droppedItem.startTime,
                            endTime = if (targetItem.startTime != null) targetItem.endTime else droppedItem.endTime,
                        )

                    onEvent(EditItineraryEvent.UpdateItineraryService(item))

                    set(fromIndex, item)
                }
                .map {
                    if (it.linkedId == droppedItem.id) {
                        val newItem = it.copy(
                            linkedId = "",
                        )

                        onEvent(EditItineraryEvent.UpdateItineraryService(newItem))

                        newItem
                    } else
                        it
                }
        }
    }

    fun reorder() {
        val fromIndex = itineraryServicesList.indexOfFirst { it.id == savedFromId }
        val toIndex = itineraryServicesList.indexOfFirst { it.id == savedToId }

        val targetItem = itineraryServicesList.getOrNull(toIndex) ?: return

        if (fromIndex != -1 && toIndex != -1) {
            itineraryServicesList = itineraryServicesList
                .toMutableList()
                .apply {
                    val item = removeAt(fromIndex)
                        .let {
                            it.copy(
                                startDate = targetItem.startDate,
                                endDate = targetItem.endDate.orEmpty(),
                                startTime = targetItem.startTime ?: it.startTime,
                                endTime = if (targetItem.startTime != null) targetItem.endTime else it.endTime,
                                linkedId = "",
                            )
                        }

                    onEvent(EditItineraryEvent.UpdateItineraryService(item))

                    var newIndex =
                        if (fromIndex > toIndex)
                            toIndex
                        else
                            toIndex - 1

                    if (savedDropToBottom)
                        newIndex += 1

                    add(newIndex, item)
                }
                .map {
                    if (it.linkedId == savedFromId) {
                        val newItem = it.copy(
                            startDate = targetItem.startDate,
                            endDate = targetItem.endDate.orEmpty(),
                            startTime = targetItem.startTime ?: it.startTime,
                            endTime = if (targetItem.startTime != null) targetItem.endTime else it.endTime,
                        )

                        onEvent(EditItineraryEvent.UpdateItineraryService(newItem))

                        newItem
                    } else
                        it
                }
        }
    }

    /**
     * Reorder the itinerary service from the given [fromId] to the given [toId].
     *
     * If [dropToBottom] is true, the item will be placed below the target item.
     * Otherwise, it will be placed above the target item.
     */
    fun reorder(fromId: String, toId: String, dropToBottom: Boolean = true) {
        if (fromId == toId)
            return

        val fromItem = itineraryServicesList.find { it.id == fromId } ?: return

        savedFromId = fromId
        savedToId = toId
        savedDropToBottom = dropToBottom

        if (fromItem.linkedId != null)
            isDisconnectServiceDialogVisible = true
        else
            reorder()
    }

    val groupedItineraryService = remember(itineraryServicesList) {
        itineraryServicesList.groupBy { it.startDate }
    }

    val itineraryServiceRectMap = remember {
        mutableStateMapOf<String, Rect>()
    }

    val isLoading = remember(state) { state.isLoading }

    DragAndDropContainer(
        state = dndState,
        modifier = modifier
            .fillMaxSize()
            .clip(RoundedCornerShape(EsConstants.RoundedCornerSize)),
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(EsConstants.ListRowVerticalSpacing),
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(scrollState)
                .padding(vertical = EsConstants.SectionVerticalPadding),
        ) {
            if (isLoading)
                EsSpinner()
            else if (itineraryServicesList.isEmpty())
                ItineraryServicesEmpty()
            else
                groupedItineraryService.forEach { (date, dayItineraryServicesList) ->
                    key("header-$date") {
                        ItineraryServicesGroupDateHeader(
                            date = date,
                            modifier = Modifier
                                .align(Alignment.Start)
                                .padding(EsConstants.SectionHorizontalPadding),
                        )
                    }

                    val groupedItineraryServiceList = remember(dayItineraryServicesList) {
                        dayItineraryServicesList
                            .groupBy { it.linkedId.orEmpty().ifEmpty { it.id } }
                            .toList()
//                            .map { (id, list) ->
//                                val mainItemIndex = list.indexOfFirst { it.id == id }
//
//                                val newList =
//                                    if (mainItemIndex != -1)
//                                        list.toMutableList().apply {
//                                            val item = removeAt(mainItemIndex)
//                                            add(0, item)
//                                        }
//                                    else
//                                        list
//
//                                id to newList
//                            }
                    }

                    val firstKey = remember(groupedItineraryServiceList) {
                        groupedItineraryServiceList.first().first
                    }

                    getReorderSeparator(
                        keyId = "separator-top-${firstKey}",
                        dndState = dndState,
                        onDrop = {
                            reorder(
                                fromId = it.data.id,
                                toId = firstKey,
                                dropToBottom = false,
                            )
                        },
                    )

                    groupedItineraryServiceList.forEach { (key, list) ->
                        Column(
                            modifier = Modifier
                                .padding(
                                    horizontal = EsConstants.SectionHorizontalPadding,
                                )
                                .border(
                                    width = 1.dp,
                                    color = if (list.size > 1) esColorScheme.primary else Color.Transparent,
                                    shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
                                )
                                .clip(
                                    RoundedCornerShape(EsConstants.RoundedCornerSize),
                                ),
                        ) {
                            list.forEachIndexed { index, itineraryService ->
                                val isSelected = rememberEquals(
                                    item = itineraryService.id,
                                    selectedIds = setOf(state.clickedItineraryServiceId),
                                ) {
                                    it
                                }

                                val focusRequester = remember { FocusRequester() }

                                key(itineraryService.id) {
                                    var isGroupDndHover by remember { mutableStateOf(false) }

                                    DraggableItem(
                                        state = dndState,
                                        key = itineraryService.id,
                                        data = itineraryService,
                                        dropStrategy = DropStrategy.CenterDistance,
                                        enabled = !isConcierge && !isUpdateLoading,
                                        draggableContent = {
                                            ItineraryServiceItem(
                                                state = state,
                                                onEvent = onEvent,
                                                onNavigation = onNavigation,
                                                isDndHover = isGroupDndHover,
                                                itineraryService = itineraryService,
                                                isSelected = isSelected,
                                                isUpdateLoading = isUpdateLoading,
                                                isConcierge = isConcierge,
                                                modifier = Modifier
                                                    .graphicsLayer { alpha = 0.4f },
                                            )
                                        },
                                        modifier = Modifier
                                            .onGloballyPositioned {
                                                itineraryServiceRectMap[itineraryService.id] = it.boundsInParent()
                                            }
                                            .dropTarget(
                                                key = itineraryService.id,
                                                state = dndState,
                                                dropAnimationEnabled = false,
                                                onDragEnter = {
                                                    isGroupDndHover = it.data.id != itineraryService.id
                                                    print(it.data.id)
                                                },
                                                onDragExit = {
                                                    isGroupDndHover = false
                                                },
                                                onDrop = {
                                                    group(
                                                        fromId = it.data.id,
                                                        toId = itineraryService.id,
                                                    )
                                                    isGroupDndHover = false
                                                },
                                            ),
                                    ) {
                                        ItineraryServiceItem(
                                            state = state,
                                            onEvent = onEvent,
                                            onNavigation = onNavigation,
                                            isDndHover = isGroupDndHover,
                                            itineraryService = itineraryService,
                                            isSelected = isSelected,
                                            isUpdateLoading = isUpdateLoading,
                                            isConcierge = isConcierge,
                                            modifier = Modifier
                                                .graphicsLayer {
                                                    alpha =
                                                        when {
                                                            isDragging ->
                                                                .1f

                                                            isGroupDndHover ->
                                                                .7f

                                                            else ->
                                                                1f
                                                        }
                                                }
                                                .upDownSelection(
                                                    focusRequester = focusRequester,
                                                    isSelected = isSelected,
                                                    list = itineraryServicesList,
                                                    key = itineraryService.id,
                                                    rectMap = itineraryServiceRectMap,
                                                    onSelect = {
                                                        onNavigation(EditItineraryNavigation.EditItineraryService(it))
                                                    },
                                                    scrollState = scrollState,
                                                    scrollTo = {
                                                        scope.launch {
                                                            scrollState.animateScrollTo(it)
                                                        }
                                                    },
                                                ),
                                        )

                                        LaunchedEffect(isSelected) {
                                            delay(100L)
                                            if (isSelected)
                                                focusRequester.requestFocus()
                                        }
                                    }
                                }
                            }
                        }

                        getReorderSeparator(
                            keyId = "separator-bottom-${key}",
                            dndState = dndState,
                            onDrop = {
                                reorder(
                                    fromId = it.data.id,
                                    toId = key,
                                    dropToBottom = false,
                                )
                            },
                        )
                    }
                }
        }
    }

    ItineraryServicesReorderConnectServiceDialog(
        fromId = savedFromId,
        toId = savedToId,
        itineraryServiceList = itineraryServicesList,
        isVisible = isConnectServiceDialogVisible,
        onDismissRequest = { isConnectServiceDialogVisible = false },
        onConfirm = { group() },
    )

    ItineraryServicesReorderDisconnectServiceDialog(
        isVisible = isDisconnectServiceDialogVisible,
        onDismissRequest = { isDisconnectServiceDialogVisible = false },
        onConfirm = { reorder() },
    )
}

@Composable
fun getReorderSeparator(
    keyId: String,
    dndState: DragAndDropState<ItineraryServiceModel>,
    onDrop: (DraggedItemState<ItineraryServiceModel>) -> Unit,
) {
    key(keyId) {
        var isDndHovered by remember { mutableStateOf(false) }

        Box(
            modifier = Modifier
                .dropTarget(
                    key = keyId,
                    state = dndState,
                    dropAnimationEnabled = false,
                    onDragEnter = {
                        isDndHovered = true
                    },
                    onDragExit = {
                        isDndHovered = false
                    },
                    onDrop = {
                        onDrop(it)
                        isDndHovered = false
                    },
                )
                .fillMaxWidth()
                .height(2.dp)
                .background(
                    if (isDndHovered)
                        MaterialTheme.esColorScheme.primary
                    else
                        Color.Transparent,
                )
                .padding(
                    horizontal = EsConstants.SectionHorizontalPadding,
                ),
        )
    }
}